import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest, LoginResponse } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedToken = localStorage.getItem('teaam_token');
        const savedUser = localStorage.getItem('teaam_user');

        if (savedToken && savedUser) {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          
          // Validate token by fetching user profile
          try {
            const profile = await authService.getProfile(savedToken);
            setUser(profile);
          } catch (error) {
            console.error('Token validation failed:', error);
            // Clear invalid token
            localStorage.removeItem('teaam_token');
            localStorage.removeItem('teaam_user');
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (token) {
      // Refresh token every 7 hours (token expires in 8 hours)
      const refreshInterval = setInterval(async () => {
        try {
          await refreshToken();
        } catch (error) {
          console.error('Token refresh failed:', error);
          logout();
        }
      }, 7 * 60 * 60 * 1000); // 7 hours

      return () => clearInterval(refreshInterval);
    }
  }, [token]);

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      setLoading(true);
      const response: LoginResponse = await authService.login(credentials);
      
      setToken(response.token);
      setUser(response.user);

      // Store in localStorage
      localStorage.setItem('teaam_token', response.token);
      localStorage.setItem('teaam_user', JSON.stringify(response.user));
      
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    
    // Clear localStorage
    localStorage.removeItem('teaam_token');
    localStorage.removeItem('teaam_user');
    
    // Call logout endpoint (fire and forget)
    if (token) {
      authService.logout(token).catch(console.error);
    }
  };

  const refreshToken = async (): Promise<void> => {
    if (!token) {
      throw new Error('No token to refresh');
    }

    try {
      const response = await authService.refreshToken(token);
      setToken(response.token);
      
      // Update localStorage
      localStorage.setItem('teaam_token', response.token);
      
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};